import _ from 'lodash';
import { Sessions } from '@or-sdk/hitl';
import { del, getCurrentInstance } from 'vue-demi';
import { getContactName } from '@onereach/hitl-base-avatars';
import { useToastV3 } from '@onereach/ui-components-25';

import { HITL, Ringtone } from '@/api';
import { useSessionsStore, useSessionsSetters } from './use-sessions-store';
import { useSessionsGetters } from './use-sessions-getters';
import { useSessionsGetActions } from './use-sessions-get-actions';
import { useAgents } from '@/modules/agents-store';
import { useEvents, useNewEvents } from '@/modules/events-store';
import { useContacts } from '@/modules/contacts-store';
import { useSessionViews } from '@/modules/session-views-store';
import { useDialerStore, useDialerActions } from '@/modules/dialer';
import { useAuth, useSentry } from '@/composables';
import { ISession, ISessionEvent } from '@/types';
import { AppRouter, notifyInBrowser, applySessionFilter } from '@/common';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';
import { modify, COMMANDS, REJECT_REASONS, CONTACT_HISTORY_STATUS } from './common';
import { AGENT_STATUS, SESSION_STATUS } from '@/constants';

export const useSessionsActions = () => {
  const store = useSessionsStore();
  const vm = getCurrentInstance()!;

  const { activeSessionId, sessionsInTimeline } = useSessionsGetters();
  const {
    upsert,
    toggleNotifyPopup,
    changeSessionStatus,
    setApplyingClaimAction,
    setApplyingStopAction,
    setApplyingTagsAction,
  } = useSessionsSetters();
  const {
    getAPISessionInitTriggers,
    getAPISessionById,
    getAPISessionsByContactId,
  } = useSessionsGetActions();
  const { activeAgentStatus } = useAgents();
  const { getAPIEventsBySessionId } = useEvents();
  const { getEventsBySessionId } = useEvents();
  const { saveReadTextEvent } = useNewEvents();
  const {
    activeContactId,
    contactsById,
    getAPIContacts,
    updateContactEventTimestamp,
  } = useContacts();
  const { activeConversationFilter } = useSessionViews();
  const { userId } = useAuth();
  const { notify } = useToastV3();
  const { captureMessage } = useSentry();

  /** Main method for [re]-initialization of sessions */
  const initializeSessions = async () => {
    const promises: Promise<any>[] = [getAPISessionInitTriggers()];
    const contactId = activeContactId.value;
    const sessionId = activeSessionId.value;

    if (sessionId) {
      promises.push(getAPISessionById({ sessionId }));
    }

    if (contactId) {
      promises.push(getAPISessionsByContactId({ contactId }));

      if (!sessionId) {
        promises.push(getAPIContacts({ contactIds: [contactId] }));
      }
    }

    try {
      await Promise.all(promises);
      if (sessionId) {
        await setActiveSession({ sessionId, contactId });
      }
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  /** Set router properties which will trigger reactive values that show UI */
  const setActiveSession = async (args: {
    sessionId: string;
    contactId?: string;
    replaceInRouter?: boolean;
  }) => {
    const { sessionId, replaceInRouter } = args;
    if (!sessionId) {
      /* De-select active session, usually used in mobile layout */
      AppRouter.appendQuery({ query: { sessionId: '' }, replace: true });
      return;
    }

    let contactId = args.contactId;
    if (!contactId) {
      const session = await getAPISessionById({ sessionId });
      contactId = session!.contactId;
    }

    AppRouter.appendQuery({
      query: { contactId, sessionId },
      replace: replaceInRouter,
    });

    /**
     * Events or contacts might or might not be requested already by other components.
     * If no actions are needed - next methods will skip the requests.
     */
    const events = getEventsBySessionId.value(sessionId);
    await Promise.all([
      getAPIContacts({ contactIds: [contactId] }),
      saveReadTextEvent({ sessionId, events }),
    ]);
  };

  /**
   * TODO: Provide contact visibility tags when creating new session to save time.
   * Original visibility tags are stored in "Contacts" tables.
   */
  const startSessionByTriggerId = async (args: {
    contactId: string;
    triggerId: string;
    params?: object;
  }) => {
    try {
      const { contactId, triggerId, params } = args;
      const contact = contactsById.value[contactId];
      const response = await HITL.sessions.startConversation({
        contactId,
        triggerId,
        params: {
          ...params,
          visibilityTags: contact?.visibilityTags,
        },
      } as any);

      const session = modify(response);
      await prepareUpsertSession({ session: response });
      await setActiveSession({ ...session, replaceInRouter: true });
      return session;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const claimSessionByAgent = async (args: {
    sessionId: string;
    params?: object;
  }) => {
    const { sessionId, params = {} } = args;
    setApplyingClaimAction(sessionId, true);
    try {
      await HITL.sessions.claimSessionByAgent({
        sessionId,
        params,
        version: 2,
      });
      await setActiveSession({ sessionId });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    } finally {
      setApplyingClaimAction(sessionId, false);
    }
  };

  const transferToBot = async (args: { sessionId: string; params?: any; }) => {
    const { sessionId, params } = args;
    setApplyingStopAction(sessionId, true);
    try {
      await HITL.sessions.transferToBot({ sessionId, params });
      changeSessionStatus({ sessionId, action: 'transfer' });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    } finally {
      setApplyingStopAction(sessionId, false);
    }
  };

  /**
   * Usually commands can be arbitrary and are re-directed to bot.
   * Commands with some default logic are wrapped in separate functions.
   */
  const applyCommandToSession = async (args: {
    sessionId: string;
    command: string;
    data?: object;
    throwOnError?: boolean;
  }) => {
    const { sessionId, command, data = {}, throwOnError } = args;
    try {
      await HITL.commands.applyCommandToSession({ command, sessionId, data });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      if (throwOnError) {
        throw error;
      }
    }
  };

  const applyMessageToSession = async (args: {
    sessionId: string;
    message?: string;
    media?: string[];
  }) => {
    const { sessionId, message, media } = args;
    try {
      await HITL.commands.applyCommandToSession({
        command: COMMANDS.SEND_MESSAGE,
        sessionId,
        data: _.omitBy({ message, media }, property => !!property),
      });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const applyRejectToSession = async (args: {
    sessionId: string;
    reason?: string;
  }) => {
    const DEFAULT_REASON = 'REJECTED';
    const { sessionId, reason = DEFAULT_REASON } = args;

    try {
      await HITL.commands.applyCommandToSession({
        command: COMMANDS.NOT_CLAIMED,
        sessionId,
        data: { reason },
      });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const applyTimeoutToSession = async (args: { sessionId: string; }) => {
    try {
      await HITL.commands.applyCommandToSession({
        command: COMMANDS.CLAIM_TIMEOUT,
        sessionId: args.sessionId,
      });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  /**
   * When session's rule tags change - session might become no longer available to user.
   * In this case we should hide session and notify user about the reason.
   */
  const disableSessionById = (args: { sessionId: string; }) => {
    const session = store.sessionsById[args.sessionId];
    if (!session) {
      return;
    }

    /* Select latest session when possible */
    del(store.sessionsById, session.sessionId);
    if (session.sessionId === activeSessionId.value) {
      const nextSessionId = sessionsInTimeline.value.length
        ? sessionsInTimeline.value.at(-1)!.sessionId
        : '';
      setActiveSession({ sessionId: nextSessionId });
    }

    const contact = contactsById.value[session.contactId] as any;
    const name = getContactName({ contact, $t: vm.proxy.$t.bind(vm.proxy) });
    const details = { contact: name, type: session.type };

    const message = session.type
      ? vm.proxy.$t('HITL.agent-ui.toasts.session-not-available', details)
      : vm.proxy.$t('HITL.agent-ui.toasts.session-not-available-empty-type', details);

    notify({ messageText: message as string, dismissible: true });
  };

  const updateSessionProperties = async (args: {
    sessionId: string;
    contactId?: string;
    tags?: string[];
    meta?: any;
  }) => {
    const { sessionId, contactId, tags, meta } = args;
    try {
      const session = await HITL.sessions.updateSession({
        sessionId,
        contactId,
        tags,
        meta,
      });

      upsert(modify(session));
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const saveTagsBySessionId = async ({ sessionId, tags }: { sessionId: string; tags: string[]; }) => {
    setApplyingTagsAction(sessionId, true);
    await updateSessionProperties({ sessionId, tags });
    setApplyingTagsAction(sessionId, false);
  };

  /**
   * Update session-related objects, like contact or events,
   * when receiving an update to a session.
   * Sessions that wouldn't be visible to an agent are skipped.
   */
  const prepareUpsertSession = async (args: { session: Sessions.Session; }) => {
    const session = modify(args.session);

    /* Dismiss popup when session is no longer claimable */
    const isClosed = session.status === SESSION_STATUS.CLOSED;
    if (store.popup.sessionId === session.sessionId) {
      if (!session.needsAction || isClosed) {
        toggleNotifyPopup();
      }
    }

    /* Consider call as complete when related session was closed */
    if (isClosed) {
      const { isDialerVisible, sessionId: dialerSessionId } = useDialerStore();
      const { onLeaveCall } = useDialerActions();
      const isSameSession = dialerSessionId.value === session.sessionId;

      if (isDialerVisible.value && isSameSession) {
        onLeaveCall();
      }
    }

    /* If new session would be hidden by the active filter */
    const sessionFilter = activeConversationFilter.value || {};
    const wouldBeVisible = store.sessionsById[session.sessionId]
      ? true
      : !!applySessionFilter([session], userId.value, sessionFilter).length;

    /* If agent hasn't interacted with the contact */
    const isHistoryTracked = store.getHistoryStatus(session.contactId)
      !== CONTACT_HISTORY_STATUS.NONE;

    if (!wouldBeVisible && !isHistoryTracked) {
      return;
    }

    const promises: Promise<any>[] = [getAPIContacts({ contactIds: [session.contactId] })];
    if (!store.sessionsById[session.sessionId]) {
      promises.push(getAPIEventsBySessionId({ sessionId: session.sessionId }));
    }

    upsert(session);
    /* New sessions move contact "up" when conversations are grouped by contact */
    updateContactEventTimestamp({
      contactId: session.contactId,
      timestamp: session.eventTimestamp,
    });
  };

  const closeSessionById = (args: { sessionId: string; }) => {
    changeSessionStatus({ sessionId: args.sessionId, action: 'close' });
  };

  /**
   * Get and save session and linked objects - contact and events.
   * Usually we request linked objects separately,
   * but in some cases, like "Notify Agent" we need all data at once.
   * TODO: Add save and reset options.
   */
  const getCompleteSessionData = async (args: {
    sessionId: string;
    contactId?: string;
  }) => {
    const { sessionId, contactId } = args;
    const promises = [
      getAPISessionById({ sessionId }),
      getAPIEventsBySessionId({ sessionId }),
    ];

    if (contactId) {
      promises.push(getAPIContacts({ contactIds: [contactId] }) as Promise<any>);
    }

    const responses = await Promise.all(promises);
    const [session, events] = responses as [ISession, ISessionEvent[]];

    const contact = contactId
      ? responses[2]
      : await getAPIContacts({ contactIds: [session.contactId] });

    /* Update meta values like timestamps, counters, etc. */
    updateContactEventTimestamp({
      contactId: session.contactId,
      timestamp: session.eventTimestamp,
    });

    return { session, contact, events };
  };

  const notifyAgent = async (args: {
    sessionId: string;
    allowBusy: boolean;
    allowOffline: boolean;
    showPopup: boolean;
    popupTemplate: object;
    notify: boolean;
    agentId: string;
  }) => {
    const { sessionId, notify, showPopup, popupTemplate, agentId } = args;
    if (!notify && !showPopup) {
      return;
    }

    if (notify) {
      /** TODO: Translate! */
      const DEFAULT_TITLE = 'Agent Required';
      const text = _.get(popupTemplate, ['text'], '');
      const title = _.get(popupTemplate, ['title'], DEFAULT_TITLE);
      const message = title + (text ? `\n${text}` : '');
      notifyInBrowser({ message, params: { sessionId } });
    }

    if (!showPopup) {
      return Ringtone.play('notify');
    }

    const allowByStatus = activeAgentStatus.value === AGENT_STATUS.AVAILABLE
      || args.allowBusy && activeAgentStatus.value === AGENT_STATUS.BUSY
      || args.allowOffline && activeAgentStatus.value === AGENT_STATUS.OFFLINE;

    if (!store.isNotifyPopupVisible && allowByStatus) {
      /* We can show popup ASAP and wait for contact to load after */
      getCompleteSessionData({ sessionId });
      captureMessage('[Notify Agent] Success', {
        event: 'NOTIFY_AGENTS',
        sessionId,
        popupTemplate,
      });

      return toggleNotifyPopup({
        sessionId,
        settings: popupTemplate,
        isContactLoading: true,
      });
    }

    if (store.isNotifyPopupVisible && agentId && allowByStatus) {
      captureMessage('[Notify Agent] Reject', {
        event: 'NOTIFY_AGENTS',
        reason: REJECT_REASONS.ANOTHER_POPUP_ACTIVE,
        sessionId,
      });

      return applyRejectToSession({
        sessionId,
        reason: REJECT_REASONS.ANOTHER_POPUP_ACTIVE,
      });
    }

    if (agentId && !allowByStatus) {
      captureMessage('[Notify Agent] Reject', {
        event: 'NOTIFY_AGENTS',
        reason: REJECT_REASONS.AGENT_UNAVAILABLE,
        sessionId,
      });

      return applyRejectToSession({
        sessionId,
        reason: REJECT_REASONS.AGENT_UNAVAILABLE,
      });
    }
  };

  return {
    initializeSessions,
    setActiveSession,
    startSessionByTriggerId,
    claimSessionByAgent,
    transferToBot,
    applyCommandToSession,
    applyMessageToSession,
    applyRejectToSession,
    applyTimeoutToSession,
    disableSessionById,
    updateSessionProperties,
    saveTagsBySessionId,
    getCompleteSessionData,
    prepareUpsertSession,
    notifyAgent,
    closeSessionById,
  };
};
